// src/pages/ExpiredSubscription.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Container,
  CircularProgress
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Shield as ShieldIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Assignment as DataIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PlanComparison from '../components/PlanComparison';
import apiClient from '../api/client';
import useSubscription from '../hooks/useSubscription';
import BillingPeriodToggle from '../components/BillingPeriodToggle';

// Get correct API base URL (same logic as api/client.js)
const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  } else {
    // Use proxy approach for better security - Vercel forwards /api/* to backend
    return '/api';
  }
};

const ExpiredSubscription = () => {
  const navigate = useNavigate();
  const [expiredInfo, setExpiredInfo] = useState(null);
  const [showPlans, setShowPlans] = useState(false);
  const [resubscribing, setResubscribing] = useState(false);
  const [error, setError] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const { reactivateSubscription } = useSubscription();

  useEffect(() => {
    // Try to extract expired subscription info from token (if still available)
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('auth_token');
      if (token) {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const decoded = JSON.parse(jsonPayload);
        
        if (decoded.cancel_at_period_end && decoded.current_period_end) {
          const expiredDate = new Date(decoded.current_period_end * 1000);
          setExpiredInfo({
            tier: decoded.subscription_tier || 'Pro',
            expiredDate: expiredDate.toLocaleDateString(),
            clinicName: decoded.clinic_name || 'Your Practice'
          });
        }
      }
    } catch (error) {
      console.log('Could not extract expiry info from token:', error);
    }

    // Fetch available plans for resubscription
    fetchAvailablePlans();
  }, []);

  const fetchAvailablePlans = async () => {
    try {
      console.log('🔍 Fetching available subscription plans (public endpoint)...');
      const response = await fetch(`${getApiBaseUrl()}/billing/public/subscription-plans`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.plans) {
        // Convert plans object to array and filter active plans
        const plansArray = Object.values(data.plans);
        const activePlans = plansArray.filter(plan => 
          plan.name !== 'internal' && plan.is_active
        ).sort((a, b) => a.tier_level - b.tier_level);
        
        console.log('✅ Available plans loaded:', activePlans.length);
        setAvailablePlans(activePlans);
      }
    } catch (error) {
      console.error('❌ Failed to fetch subscription plans:', error);
      setError('Failed to load subscription plans');
    }
  };

  const handleReactivateSamePlan = async () => {
    // For expired users, we need to use checkout sessions since they can't access authenticated endpoints
    // We'll try to determine their previous plan from the JWT token and match it with available plans
    try {
      if (availablePlans.length === 0) {
        console.warn('⚠️ No plans loaded yet, falling back to plan selection');
        setShowPlans(true);
        return;
      }

      const token = localStorage.getItem('authToken') || localStorage.getItem('auth_token');
      let previousTier = 'pro'; // Default fallback
      
      if (token) {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const decoded = JSON.parse(jsonPayload);
        previousTier = decoded.subscription_tier || 'pro';
      }
      
      console.log('🔄 Looking for previous plan:', previousTier);
      
      // Find the matching plan from available plans
      const matchingPlan = availablePlans.find(plan => 
        plan.name === previousTier ||
        plan.display_name.toLowerCase() === previousTier.toLowerCase()
      );
      
      if (matchingPlan) {
        console.log('✅ Found matching plan:', matchingPlan.display_name);
        const plan = {
          name: matchingPlan.display_name,
          priceId: matchingPlan.stripe_price_id
        };
        await handleCreateCheckoutSession(plan);
      } else {
        console.warn('⚠️ Could not find matching plan for:', previousTier);
        console.log('Available plans:', availablePlans.map(p => p.name));
        // Fallback to the first available plan (usually cheapest)
        const fallbackPlan = availablePlans[0];
        if (fallbackPlan) {
          console.log('Using fallback plan:', fallbackPlan.display_name);
          const plan = {
            name: fallbackPlan.display_name,
            priceId: fallbackPlan.stripe_price_id
          };
          await handleCreateCheckoutSession(plan);
        } else {
          throw new Error('No available plans found');
        }
      }
      
    } catch (error) {
      console.error('❌ Failed to determine previous plan:', error);
      setError(`Failed to find your previous plan: ${error.message}`);
      // Fallback to plan selection if we can't determine previous plan
      setShowPlans(true);
    }
  };

  const handleResubscribe = () => {
    setShowPlans(true);
  };

  const handlePlanUpgrade = async (plan, proration) => {
    // Plan now includes priceId for the selected billing period
    await handleCreateCheckoutSession(plan);
  };

  const handlePlanDowngrade = async (plan, proration) => {
    // Plan now includes priceId for the selected billing period  
    await handleCreateCheckoutSession(plan);
  };

  const handleCreateCheckoutSession = async (plan) => {
    setResubscribing(true);
    setError(null);
    
    try {
      console.log('🔄 Creating checkout session for plan (public endpoint):', plan.name);
      
      // Extract clinic ID from stored value or token for the public endpoint
      let clinicId = localStorage.getItem('expired_clinic_id');
      console.log('🔍 Stored expired_clinic_id:', clinicId);
      
      if (!clinicId) {
        // Fallback: try to extract from token (in case user navigated directly)
        console.log('🔍 No stored clinic ID, trying to extract from token...');
        try {
          const token = localStorage.getItem('authToken') || localStorage.getItem('auth_token');
          console.log('🔍 Found token:', !!token);
          if (token) {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
              return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            const decoded = JSON.parse(jsonPayload);
            console.log('🔍 Decoded token clinic_id:', decoded.clinic_id);
            clinicId = decoded.clinic_id;
          }
        } catch (e) {
          console.warn('Could not extract clinic ID from token:', e);
        }
      }
      
      console.log('🔍 Final clinic ID for checkout:', clinicId);
      
      if (!clinicId) {
        console.error('❌ No clinic ID available from any source');
        console.log('   - localStorage expired_clinic_id:', localStorage.getItem('expired_clinic_id'));
        console.log('   - localStorage authToken:', !!localStorage.getItem('authToken'));
        console.log('   - localStorage auth_token:', !!localStorage.getItem('auth_token'));
        throw new Error('Cannot identify clinic - please log in again');
      }
      
      console.log('🔍 Using clinic ID for checkout session:', clinicId);
      
      // Redirect to our success handler page that will manage JWT refresh
      // Include clinic ID in the URL since localStorage might not persist across Stripe redirect
      const successUrl = `${window.location.origin}/subscription-success?session_id={CHECKOUT_SESSION_ID}&clinic_id=${clinicId}`;
      
      const response = await fetch(`${getApiBaseUrl()}/billing/public/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Clinic-ID': clinicId
        },
        body: JSON.stringify({
          price_id: plan.priceId,
          success_url: successUrl,
          cancel_url: `${window.location.origin}/expired-subscription`,
          // For resubscription, don't include trial days since this is a renewal
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        console.log('✅ Checkout session created, redirecting to Stripe...');
        // Clean up the pre-expiry URL and stored clinic ID since we've used them
        localStorage.removeItem('pre_expiry_url');
        localStorage.removeItem('expired_clinic_id');
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('❌ Checkout session creation failed:', error);
      setError(`Failed to start checkout: ${error.message}`);
      setResubscribing(false);
    }
  };

  const handleLoginRedirect = () => {
    // Clear expired token and redirect to login
    localStorage.removeItem('authToken');
    localStorage.removeItem('auth_token');
    navigate('/login');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <ScheduleIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
        <Typography variant="h3" component="h1" gutterBottom fontWeight="600">
          Your Subscription Has Expired
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
          Your access to MedInsight Pro ended on {expiredInfo?.expiredDate || 'your last billing date'}. 
          Don't worry - all your data is safely preserved and ready for immediate restoration.
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Data Safety Assurance */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid', borderColor: 'success.light' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DataIcon sx={{ color: 'success.main', mr: 1 }} />
                <Typography variant="h6" fontWeight="600">
                  Your Data Is Safe
                </Typography>
              </Box>
              <Typography variant="body1" paragraph>
                All your practice data, reports, settings, and configurations are securely preserved:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Historical patient and revenue data" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Custom reports and analytics" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Integration settings and API configs" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                  <ListItemText primary="User preferences and dashboard layouts" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* What You're Missing */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid', borderColor: 'warning.light' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WarningIcon sx={{ color: 'warning.main', mr: 1 }} />
                <Typography variant="h6" fontWeight="600">
                  What You're Missing
                </Typography>
              </Box>
              <Typography variant="body1" paragraph>
                Without an active subscription, you can't access:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                  <ListItemText primary="Real-time data sync from Pabau" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                  <ListItemText primary="Analytics dashboard and reports" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                  <ListItemText primary="API integrations and automations" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                  <ListItemText primary="Patient insights and revenue tracking" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Resubscribe Section */}
      <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'primary.light', color: 'primary.contrastText', mb: 4 }}>
        <ShieldIcon sx={{ fontSize: 48, mb: 2 }} />
        <Typography variant="h4" fontWeight="600" gutterBottom>
          Get Back Your Protection - Instantly
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
          Resubscribe now and regain immediate access to all your data and insights. 
          Your dashboard will be restored exactly as you left it - no setup required!
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3, mx: 'auto', maxWidth: 600 }}>
            {error}
          </Alert>
        )}

        {!showPlans && !resubscribing && (
          <Box>
            <Button
              variant="contained"
              size="large"
              onClick={handleReactivateSamePlan}
              startIcon={<RefreshIcon />}
              sx={{ 
                mr: 2, 
                py: 1.5, 
                px: 4, 
                fontSize: '1.1rem',
                fontWeight: 600,
                bgcolor: 'success.main',
                '&:hover': { bgcolor: 'success.dark' }
              }}
            >
              Reactivate {expiredInfo?.tier || 'Previous'} Plan
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={handleResubscribe}
              sx={{ 
                mr: 2,
                py: 1.5, 
                px: 4, 
                fontSize: '1.1rem',
                borderColor: 'primary.contrastText',
                color: 'primary.contrastText',
                '&:hover': { 
                  borderColor: 'primary.contrastText',
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Choose Different Plan
            </Button>
            <Button
              variant="text"
              size="large"
              onClick={handleLoginRedirect}
              sx={{ 
                py: 1.5, 
                px: 4, 
                fontSize: '1.1rem',
                color: 'primary.contrastText',
                '&:hover': { 
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Back to Login
            </Button>
          </Box>
        )}

        {resubscribing && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress sx={{ mr: 2, color: 'primary.contrastText' }} />
            <Typography variant="h6" sx={{ color: 'primary.contrastText' }}>
              Processing your resubscription...
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Plan Selection */}
      {showPlans && !resubscribing && (
        <Box>
          <Typography variant="h4" textAlign="center" gutterBottom fontWeight="600" sx={{ mb: 3 }}>
            Choose Your Plan
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="600">
              Instant Restoration: Your subscription activates immediately after payment, 
              and all your data will be available within seconds.
            </Typography>
          </Alert>
          
          <PlanComparison
            onUpgrade={handlePlanUpgrade}
            onDowngrade={handlePlanDowngrade}
            onNotification={() => {}} // Not needed for this flow
            isResubscription={true}
          />
          
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => { setShowPlans(false); setError(null); }}
              sx={{ mr: 2 }}
            >
              Back
            </Button>
            <Button
              variant="text"
              onClick={handleLoginRedirect}
            >
              Return to Login Instead
            </Button>
          </Box>
        </Box>
      )}

      {/* Loading state for plan selection */}
      {showPlans && resubscribing && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h5" gutterBottom>
            Creating your checkout session...
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You'll be redirected to Stripe to complete your payment
          </Typography>
        </Box>
      )}

      {/* Help Section */}
      <Box sx={{ textAlign: 'center', mt: 4, pt: 4, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Need Help Getting Back In?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Our support team is here to help you restore your access quickly.
        </Typography>
        <Button
          variant="outlined"
          href="mailto:support@medinsight.pro"
          sx={{ mr: 2 }}
        >
          Email Support
        </Button>
        <Button
          variant="outlined"
          href="https://support.medinsightpro.com"
          target="_blank"
        >
          Help Center
        </Button>
      </Box>
    </Container>
  );
};

export default ExpiredSubscription;